/**
 * WidgetKeysPage — admin surface for managing tenant widget API keys.
 *
 * Widget keys are embedded in customer HTML (not secret) but the `enabled`
 * flag controls whether the key accepts visitor sessions. Admins can:
 *   - List all keys
 *   - Create a new key (key value generated server-side)
 *   - Enable/disable keys inline
 *   - Rotate a key (generates a new key value)
 *   - Delete a key
 *
 * Permission: `tenant:settings_manage` (TENANT_SETTINGS_MANAGE).
 */
import { useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { InputSwitch } from 'primereact/inputswitch'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'
import { z } from 'zod'

import PageHeader from '../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../components/admin/ResourceTable.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import ResourceDrawer from '../../components/admin/ResourceDrawer.js'
import { ZodForm, FormText, FormSwitch } from '../../components/forms/index.js'
import { useAdminResource } from '../../hooks/useAdminApi.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatDate } from '../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WidgetKey {
  id: string
  key: string
  label: string
  enabled: boolean
  createdAt: string
}

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

const CreateSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  enabled: z.boolean(),
})

type CreateFormValues = z.infer<typeof CreateSchema>

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WidgetKeysPage(): JSX.Element {
  const toast = useToast()
  const { items: keys, loading, refetch } = useAdminResource<WidgetKey>(
    '/admin/widget-keys',
    { responseKey: 'keys' },
  )

  const [drawerOpen, setDrawerOpen] = useState(false)

  async function handleToggleEnabled(k: WidgetKey): Promise<void> {
    try {
      await api.patch(`/admin/widget-keys/${k.id}`, { enabled: !k.enabled })
      refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update key')
      refetch()
    }
  }

  async function handleRotate(k: WidgetKey): Promise<void> {
    confirmDialog({
      message: `Rotate key "${k.label}"? Existing widget deployments using the current key will stop working until updated.`,
      header: 'Rotate Widget Key',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-warning',
      accept: async () => {
        try {
          await api.post(`/admin/widget-keys/${k.id}/rotate`, {})
          toast.success('Widget key rotated')
          refetch()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to rotate key')
        }
      },
    })
  }

  function handleDelete(k: WidgetKey): void {
    confirmDialog({
      message: `Permanently delete widget key "${k.label}"? Existing deployments will immediately stop accepting visitors.`,
      header: 'Delete Widget Key',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.delete(`/admin/widget-keys/${k.id}`)
          toast.success('Widget key deleted')
          refetch()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to delete key')
        }
      },
    })
  }

  async function handleCreate(data: CreateFormValues): Promise<void> {
    try {
      await api.post('/admin/widget-keys', data)
      toast.success('Widget key created')
      refetch()
      setDrawerOpen(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create key')
    }
  }

  function enabledCell(k: WidgetKey): JSX.Element {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <InputSwitch
          checked={k.enabled}
          aria-label={`Enabled: ${k.label}`}
          onChange={() => void handleToggleEnabled(k)}
        />
      </div>
    )
  }

  function actionsCell(k: WidgetKey): JSX.Element {
    return (
      <div className="flex gap-2">
        <Button
          label="Rotate"
          size="small"
          severity="warning"
          text
          onClick={() => void handleRotate(k)}
        />
      </div>
    )
  }

  const columns: ColumnDef<WidgetKey>[] = [
    { label: 'Label', accessor: 'label' },
    { label: 'Key', accessor: (k) => <code className="text-xs">{k.key}</code> },
    { label: 'Enabled', accessor: (k) => enabledCell(k) },
    { label: 'Created', accessor: (k) => formatDate(k.createdAt) },
    { label: '', accessor: (k) => actionsCell(k) },
  ]

  return (
    <RequirePermission permission="tenant:settings_manage">
      <div className="space-y-4 p-4">
        <PageHeader
          title="Widget Keys"
          description="API keys embedded in chat widget deployments. Disable a key to block all new visitor sessions."
          actions={
            <Button
              label="+ New Widget Key"
              onClick={() => setDrawerOpen(true)}
            />
          }
        />

        <ResourceTable<WidgetKey>
          columns={columns}
          rows={keys}
          loading={loading}
          actions={['delete']}
          onDelete={handleDelete}
          empty={{
            title: 'No widget keys',
            description: 'Create a widget key to embed the chat widget on your site.',
            icon: 'pi pi-key',
          }}
        />

        <ResourceDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSave={async () => {}}
          title="New Widget Key"
        >
          <ZodForm
            schema={CreateSchema}
            defaultValues={{ label: '', enabled: true }}
            onSubmit={handleCreate}
            render={() => (
              <>
                <FormText name="label" label="Label" required autoFocus />
                <FormSwitch name="enabled" label="Enabled" />
                <button
                  type="submit"
                  className="mt-4 rounded bg-[#16273F] px-4 py-2 text-white"
                >
                  Create
                </button>
              </>
            )}
          />
        </ResourceDrawer>

        <ConfirmDialog />
      </div>
    </RequirePermission>
  )
}
